import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { type SearchResult, buildSearchIndex, filterSearchIndex } from '../features/search/index';

/**
 * Overlay de recherche globale, invoqué via Ctrl/Cmd+K ou le bouton de la topbar.
 * Affiche une liste de résultats typés cliquables.
 */

@customElement('cof2-search-overlay')
export class Cof2SearchOverlay extends LitElement {
  @state() private open = false;
  @state() private query = '';
  @state() private results: readonly SearchResult[] = [];
  @state() private indexLoaded = false;

  static styles = css`
    .backdrop {
      position: fixed;
      inset: 0;
      background: rgb(0 0 0 / 0.5);
      display: grid;
      place-items: start center;
      padding-top: 10vh;
      z-index: 200;
    }
    .panel {
      background: var(--color-bg-elev-2);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-lg);
      width: min(640px, 90vw);
      padding: var(--space-3);
      box-shadow: 0 20px 60px rgb(0 0 0 / 0.5);
    }
    input {
      width: 100%;
      padding: var(--space-2) var(--space-3);
      background: var(--color-bg);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      color: var(--color-fg);
      font-size: var(--font-size-lg);
    }
    input:focus-visible {
      outline: 2px solid var(--color-accent);
    }
    ul {
      list-style: none;
      margin: var(--space-3) 0 0;
      padding: 0;
      max-height: 50vh;
      overflow: auto;
    }
    li {
      padding: var(--space-2) var(--space-3);
      border-radius: var(--radius-sm);
    }
    li a {
      display: block;
      color: inherit;
      text-decoration: none;
    }
    li:hover,
    li:focus-within {
      background: var(--color-bg-elev-1);
    }
    .title {
      font-weight: 600;
      color: var(--color-accent);
    }
    .kind {
      display: inline-block;
      font-size: var(--font-size-xs);
      color: var(--color-fg-muted);
      margin-left: var(--space-2);
    }
    .sub {
      font-size: var(--font-size-sm);
      color: var(--color-fg-muted);
    }
    .snippet {
      font-size: var(--font-size-sm);
      margin-top: var(--space-1);
      color: var(--color-fg);
    }
    .empty {
      padding: var(--space-5);
      text-align: center;
      color: var(--color-fg-muted);
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onGlobalKey);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onGlobalKey);
  }

  private onGlobalKey = (e: KeyboardEvent): void => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.openPanel();
    } else if (e.key === 'Escape' && this.open) {
      this.close();
    }
  };

  openPanel(): void {
    this.open = true;
    this.query = '';
    this.results = [];
    if (!this.indexLoaded) {
      void buildSearchIndex().then(() => {
        this.indexLoaded = true;
      });
    }
    requestAnimationFrame(() => {
      this.renderRoot.querySelector<HTMLInputElement>('input')?.focus();
    });
  }

  close(): void {
    this.open = false;
  }

  private async onInput(e: Event): Promise<void> {
    const q = (e.target as HTMLInputElement).value;
    this.query = q;
    if (!q.trim()) {
      this.results = [];
      return;
    }
    const index = await buildSearchIndex();
    this.indexLoaded = true;
    this.results = filterSearchIndex(index, q, 30);
  }

  private onBackdrop(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.close();
  }

  private onResultClick(): void {
    // Laisse le hashchange faire son job, puis ferme.
    requestAnimationFrame(() => this.close());
  }

  render() {
    if (!this.open) return null;
    return html`
      <div class="backdrop" @click=${this.onBackdrop}>
        <div class="panel" role="dialog" aria-modal="true" aria-label="Recherche globale">
          <input
            type="search"
            placeholder="Rechercher profil, voie, capacité, créature, règle..."
            @input=${this.onInput}
            .value=${this.query}
            aria-label="Recherche globale"
          />
          ${
            this.query.trim() && this.results.length === 0 && this.indexLoaded
              ? html`<div class="empty">Aucun résultat pour "${this.query}".</div>`
              : null
          }
          ${
            this.query.trim() && !this.indexLoaded
              ? html`<div class="empty">Indexation en cours...</div>`
              : null
          }
          ${
            this.results.length > 0
              ? html`<ul>
                ${this.results.map(
                  (r) => html`<li>
                    <a href=${r.href} @click=${this.onResultClick}>
                      <span class="title">${r.title}</span><span class="kind">· ${r.kind}</span>
                      <div class="sub">${r.subtitle}</div>
                      ${r.snippet ? html`<div class="snippet">${r.snippet}</div>` : null}
                    </a>
                  </li>`,
                )}
              </ul>`
              : null
          }
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cof2-search-overlay': Cof2SearchOverlay;
  }
}
